/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useEffect } from 'react';
import { Label, NativeInput, Text } from '@/shared/ui';
import {
  clampLeadAdRadiusKm,
  isValidLeadAdUrl,
  MAX_LEAD_AD_RADIUS_KM,
  MIN_LEAD_AD_RADIUS_KM,
} from '@/shared/utils/ad-dialog-helpers';
import { isProhibitedLeadAdQuestion } from '@/shared/utils/lead-ad-question-policy';

/** Shared Tailwind class string for all Lead Ad text inputs. */
export const LEAD_AD_INPUT_CLASSES = 'w-full px-(--space-2) py-(--space-1-5) text-sm rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-primary) text-(--text-primary) outline-none transition-colors focus:border-(--seed-base) focus:ring-1 focus:ring-(--seed-base)';

/** The Lead Ad content/targeting options an agency can set, shared by Facebook and Instagram. */
export interface LeadAdContentValues {
  headline: string;
  closing: string;
  radiusKm: number;
  formQuestion: string;
  thankYouMessage: string;
  websiteUrl: string;
  privacyUrl: string;
}

/** Props for the Lead Ad content/targeting field group. */
export interface LeadAdContentFieldsProps {
  /** The committed (saved) values the draft starts from and is compared against. */
  values: LeadAdContentValues;
  /** Reports the draft up to the modal, whose single Save commits it. */
  onDraftChange: (draft: LeadAdContentValues, state: { dirty: boolean; valid: boolean }) => void;
  t: (key: string, opts?: Record<string, string | number>) => string;
}

type TextKey = Exclude<keyof LeadAdContentValues, 'radiusKm'>;

const TEXT_FIELDS: ReadonlyArray<{ key: TextKey; max: number; url?: boolean }> = [
  { key: 'headline', max: 40 },
  { key: 'closing', max: 100 },
  { key: 'formQuestion', max: 80 },
  { key: 'thankYouMessage', max: 150 },
  { key: 'websiteUrl', max: 500, url: true },
  { key: 'privacyUrl', max: 500, url: true },
];

/**
 * Same values.
 */
function sameValues(a: LeadAdContentValues, b: LeadAdContentValues): boolean {
  return a.radiusKm === b.radiusKm && TEXT_FIELDS.every(({ key }) => a[key] === b[key]);
}

/**
 * The shared "content and audience" block of the Lead Ad modal: the ad
 * headline, closing sentence, form question, thank-you text and links, plus
 * the targeting radius. Owns only the in-progress draft -- saving is the
 * modal's single global action.
 *
 * There is deliberately no age or gender field: Lead Ads are Housing ads,
 * for which Meta fixes the audience at 18-65 and forbids narrowing it.
 *
 * @param props - See {@link LeadAdContentFieldsProps}.
 * @returns The fields block.
 */
export function LeadAdContentFields({ values, onDraftChange, t }: LeadAdContentFieldsProps) {
  const [draft, setDraft] = useState<LeadAdContentValues>(values);

  // Keyed on the content, not the object: the parent rebuilds `values` on
  // every render and that must not wipe what the agency is typing.
  const committedKey = JSON.stringify(values);
  useEffect(() => {
    setDraft(JSON.parse(committedKey));
  }, [committedKey]);

  // A form question on Meta's prohibited list is never run.
  const questionProhibited = isProhibitedLeadAdQuestion(draft.formQuestion);
  const valid = TEXT_FIELDS.every(({ key, url }) => !url || isValidLeadAdUrl(draft[key])) && !questionProhibited;
  const dirty = !sameValues(draft, values);

  useEffect(() => {
    onDraftChange(
      { ...draft, radiusKm: clampLeadAdRadiusKm(draft.radiusKm) },
      { dirty, valid },
    );
  }, [draft, dirty, valid, onDraftChange]);

  const setText = (key: TextKey, value: string) => setDraft((prev) => ({ ...prev, [key]: value }));
  const prefix = 'facebook.leadAdSection.content';

  return (
    <div className="flex flex-col gap-(--space-4) rounded-(--radius-lg) border border-(--border-subtle) p-(--space-4)" data-testid="lead-ad-content-fields">
      <div className="flex flex-col gap-(--space-1)">
        <Text className="m-0 text-[length:var(--text-body-sm)] [font-weight:var(--font-semibold)] text-(--text-primary)">{t(`${prefix}.title`)}</Text>
        <Text className="m-0 text-[length:var(--text-small)] leading-[var(--leading-normal)] text-(--text-secondary)">{t(`${prefix}.description`)}</Text>
      </div>

      {TEXT_FIELDS.map(({ key, max, url }) => {
        const invalid = url && !isValidLeadAdUrl(draft[key]);
        const prohibited = key === 'formQuestion' && questionProhibited;
        return (
          <Label key={key} className="flex flex-col gap-(--space-1-5)">
            <Text component="span" className="text-xs [font-weight:var(--font-medium)] text-(--text-secondary)">{t(`${prefix}.${key}Label`)}</Text>
            <NativeInput
              type={url ? 'url' : 'text'}
              maxLength={max}
              value={draft[key]}
              onChange={(e) => setText(key, e.target.value)}
              className={LEAD_AD_INPUT_CLASSES}
              data-testid={`lead-ad-content-${key}`}
            />
            <Text component="span" className="text-xs leading-relaxed text-(--text-tertiary)">{t(`${prefix}.${key}Hint`)}</Text>
            {invalid && (
              <Text component="span" className="text-xs text-(--status-error)" data-testid={`lead-ad-content-${key}-invalid`}>
                {t(`${prefix}.urlInvalid`)}
              </Text>
            )}
            {url && !draft[key].trim() && (
              <Text component="span" className="text-xs text-(--status-warning-text)" data-testid={`lead-ad-content-${key}-required`}>
                {t(`${prefix}.requiredField`)}
              </Text>
            )}
            {prohibited && (
              <Text component="span" className="text-xs text-(--status-error)" data-testid={`lead-ad-content-${key}-problem`}>
                {t(`${prefix}.formQuestionProhibited`)}
              </Text>
            )}
          </Label>
        );
      })}

      <Label className="flex flex-col gap-(--space-1-5) w-40">
        <Text component="span" className="text-xs [font-weight:var(--font-medium)] text-(--text-secondary)">{t(`${prefix}.radiusKmLabel`)}</Text>
        <NativeInput
          type="number"
          min={MIN_LEAD_AD_RADIUS_KM}
          max={MAX_LEAD_AD_RADIUS_KM}
          value={draft.radiusKm}
          onChange={(e) => setDraft((prev) => ({ ...prev, radiusKm: Number(e.target.value) }))}
          onBlur={() => setDraft((prev) => ({ ...prev, radiusKm: clampLeadAdRadiusKm(prev.radiusKm) }))}
          className={LEAD_AD_INPUT_CLASSES}
          data-testid="lead-ad-content-radiusKm"
        />
        <Text component="span" className="text-xs leading-relaxed text-(--text-tertiary)">
          {t(`${prefix}.radiusKmHint`, { min: MIN_LEAD_AD_RADIUS_KM, max: MAX_LEAD_AD_RADIUS_KM })}
        </Text>
      </Label>

      <Text className="m-0 text-xs leading-relaxed text-(--text-tertiary)" data-testid="lead-ad-audience-note">
        {t(`${prefix}.audienceNote`)}
      </Text>
    </div>
  );
}
